import { Config } from 'ha/config';
import { init, setAnonymousId } from '@hbf/analytics';
import { reportError } from 'ha/helpers/bugsnag/reportError';
import { isIntercomInitialised } from 'ha/modules/Intercom';

type Callback = (...args: unknown[] | undefined[]) => void;

const analytics = (config: Config['analytics']) => {
  const callbacks: Callback[] = [];
  const userDependantCallbacks: Callback[] = [];

  let userIdentified = false;

  function runUserIdentifiedCallbacks() {
    if (global.mixpanel) {
      userDependantCallbacks.forEach(cb => cb(global.mixpanel));
      userDependantCallbacks.length = 0;
    }
  }

  return {
    init: ({
      withIntercom = true,
      withOneTrust,
      lockIntegrationsVersion = false,
    }: {
      withIntercom: boolean;
      withOneTrust: boolean;
      lockIntegrationsVersion: boolean;
    }) => {
      if (config.enabled) {
        return init({
          rudderstack: {
            writeKey: config.rudderStackWriteKey,
            dataPlaneUrl: config.rudderStackDataPlaneURL,
            configUrl: config.rudderStackSourceConfig,
            cdnUrl: config.rudderStackCDN,
            loadOptions: {
              integrations: {
                Intercom: withIntercom,
              },
              cookieConsentManager: {
                oneTrust: {
                  enabled: withOneTrust,
                },
              },
              lockIntegrationsVersion,
            },
            onReady: () => {
              try {
                // Why do we need this when Rudderstack defines its own anonymousId?
                // It's so we do not lose track of users who have already been identified
                // in Mixpanel but might be navigating the app without being logged in.
                // We all hope to address this very soon.
                const mixpanelCookie = document.cookie.match(
                  /ajs_distinct_id=[^;]*/g,
                );
                let distinctId = '';
                if (mixpanelCookie) {
                  distinctId = mixpanelCookie[0].replace(
                    'ajs_distinct_id=',
                    '',
                  );
                }

                if (distinctId !== '') {
                  setAnonymousId(distinctId);
                }
              } catch (error) {
                reportError('Rudderstack - reading Mixpanel cookie ', {
                  metaData: { error },
                });
              }

              callbacks.forEach(callback => callback());
              callbacks.length = 0;
            },
          },
        })
          .then(() => {
            // eslint-disable-next-line no-console
            console.log('Rudderstack initialized');
          })
          .catch(error => {
            reportError('Rudderstack - init', { metaData: { error } });
          });
      }

      return Promise.resolve();
    },
    onMixpanelReady: (callback: Callback) => {
      if (global.mixpanel) {
        callback(global.mixpanel);
      } else {
        callbacks.push(() => callback(global.mixpanel));
      }
    },
    onUserIdentified: (callback: Callback) => {
      if (userIdentified) {
        callback(global.mixpanel);
      } else {
        userDependantCallbacks.push(callback);
      }
    },
    identifyUser: () => {
      runUserIdentifiedCallbacks();
      userIdentified = true;
    },
    onIntercomReady: (callback: Callback) => {
      if (isIntercomInitialised()) {
        callback();
      } else {
        callbacks.push(callback);
      }
    },
  };
};
export { analytics };
